import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { company, interestOptions, img } from "../mock";
import { useToast } from "../hooks/use-toast";
import Seo from "../components/Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", interest: interestOptions[0], message: "" };

const Contact = () => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const obs = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("in")), { threshold: 0.1 });
    document.querySelectorAll(".mh-reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete the required fields", description: "Name, email and message are required." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: "Thank you for reaching out!", description: "Your message has been sent. A member of the Monarch Homes team will be in touch shortly." });
      setForm(empty);
    } catch (err) {
      toast({ title: "Something went wrong", description: "We couldn't send your message. Please call us at " + company.phone + "." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Seo
        title="Contact Us"
        description="Contact Monarch Homes of Brevard. Call (321) 777-2701 or send a message about building your custom home on the Space Coast. Office in Satellite Beach, FL."
        keywords="contact Monarch Homes of Brevard, custom home builder Satellite Beach FL"
        path="/contact"
      />
      <section className="mh-page-hero" style={{ backgroundImage: `url(${img.living1})` }}>
        <div className="mh-page-hero-inner">
          <div className="mh-crumb"><Link to="/">Home</Link> / Contact Us</div>
          <h1>Contact Us</h1>
        </div>
      </section>

      <section className="mh-section">
        <div className="mh-container">
          <div className="mh-section-head mh-reveal">
            <div className="mh-eyebrow">Let&rsquo;s Talk</div>
            <h2>Have A Question Or Want Information?</h2>
            <p>We would love to hear from you! Send us a message and a member of our family-owned team will reach out.</p>
            <div className="mh-divider" />
          </div>

          <div className="mh-contact-grid">
            <form className="mh-form mh-reveal" onSubmit={onSubmit}>
              <h3>Send Us A Message</h3>
              <p className="sub">Fields marked <span style={{ color: "var(--amber)" }}>*</span> are required.</p>
              <div className="mh-form-row">
                <div className="mh-field">
                  <label>Name <span className="req">*</span></label>
                  <input name="name" value={form.name} onChange={onChange} placeholder="Your full name" />
                </div>
                <div className="mh-field">
                  <label>Phone</label>
                  <input name="phone" value={form.phone} onChange={onChange} placeholder="(321) 000-0000" />
                </div>
              </div>
              <div className="mh-field">
                <label>Email <span className="req">*</span></label>
                <input type="email" name="email" value={form.email} onChange={onChange} placeholder="you@email.com" />
              </div>
              <div className="mh-field">
                <label>I&rsquo;m Interested In</label>
                <select name="interest" value={form.interest} onChange={onChange}>
                  {interestOptions.map((o) => (<option key={o} value={o}>{o}</option>))}
                </select>
              </div>
              <div className="mh-field">
                <label>How Can We Help You? <span className="req">*</span></label>
                <textarea name="message" value={form.message} onChange={onChange} placeholder="Tell us about your project, lot, or the home you're looking for..." />
              </div>
              <button type="submit" className="mh-cta-btn" disabled={submitting}>
                {submitting ? (<><Loader2 size={18} className="mh-spin" /> Sending...</>) : (<>Send Message <Send size={16} /></>)}
              </button>
            </form>

            <div className="mh-info-panel mh-reveal">
              <h3>Get In Touch</h3>
              <div className="mh-info-row">
                <div className="ic"><MapPin size={20} /></div>
                <div><h5>Office</h5><span>{company.address}<br />{company.cityStateZip}</span></div>
              </div>
              <div className="mh-info-row">
                <div className="ic"><Phone size={20} /></div>
                <div><h5>Phone</h5><a href={company.phoneHref}>{company.phone}</a></div>
              </div>
              <div className="mh-info-row">
                <div className="ic"><Mail size={20} /></div>
                <div><h5>Email</h5><a href={`mailto:${company.email}`}>{company.email}</a></div>
              </div>
              <div className="mh-info-row">
                <div className="ic"><Clock size={20} /></div>
                <div><h5>Hours</h5><span>{company.hours}</span></div>
              </div>
              <div className="mh-map">
                <iframe title="Monarch Homes of Brevard location" loading="lazy" src="https://www.google.com/maps?q=1275+South+Patrick+Dr,+Satellite+Beach,+FL+32937&output=embed" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
